import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../config/routes";
import { useProperty } from "../../contexts/PropertyContext";
import {
  ArrowRight,
  Package,
  Wrench,
  Calendar,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle2,
  Clock,
  Activity,
  Zap,
  Shield,
  ChevronRight,
  Home,
  Wifi,
  BarChart3,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import {
  useDashboardData,
  UpcomingTask,
  AssetCategory,
} from "../../hooks/useDashboardData";
import { Asset, toAbsoluteImageUrl } from "../../services/asset.api";
import { PromoAdsCarousel } from "../../components/dashboard/PromoAdsCarousel";
import { CriticalAlertsSection } from "../../components/dashboard/CriticalAlertsSection";

// ─── HELPERS ────────────────────────────────────────────────────────────────

/** Icon per asset category name */
function getCategoryIcon(category: string) {
  const lower = category.toLowerCase();
  if (lower.includes("hvac") || lower.includes("cool") || lower.includes("heat")) return Zap;
  if (lower.includes("kitchen") || lower.includes("appliance")) return Home;
  if (lower.includes("smart") || lower.includes("device")) return Wifi;
  if (lower.includes("plumb") || lower.includes("water")) return AlertCircle;
  if (lower.includes("security")) return Shield;
  return Package;
}

const getGreeting = (t: (key: string) => string) => {
  const hour = new Date().getHours();
  if (hour < 12) return t('greeting.morning');
  if (hour < 18) return t('greeting.afternoon');
  return t('greeting.evening');
};

function StatSkeleton() {
  return (
    <div className="rounded-2xl border bg-card p-6 animate-pulse">
      <div className="h-3 w-24 bg-secondary rounded mb-3" />
      <div className="h-8 w-16 bg-secondary rounded mb-2" />
      <div className="h-3 w-20 bg-secondary rounded" />
    </div>
  );
}

function SectionSkeleton({ rows = 3 }: Readonly<{ rows?: number }>) {
  const keys = React.useMemo(() => Array.from({ length: rows }, (_, i) => `sk-${i}`), [rows]);
  return (
    <div className="space-y-3 animate-pulse">
      {keys.map((k) => (
        <div key={k} className="h-14 bg-secondary/40 rounded-xl" />
      ))}
    </div>
  );
}

function computeHeroSubtitle(
  isLoading: boolean,
  openIssues: number,
  dueMaintenance: number,
  totalAssets: number,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (isLoading) return t('hero.loading');
  if (openIssues > 0) {
    return t('hero.openIssues', { count: openIssues });
  }
  if (dueMaintenance > 0) {
    return t('hero.dueMaintenance', { count: dueMaintenance });
  }
  if (totalAssets === 0) return t('hero.noAssets');
  return t('hero.allGood', { count: totalAssets });
}

function computeBadgeConfig(
  isLoading: boolean,
  openIssues: number,
  t: (key: string, options?: Record<string, unknown>) => string,
): { text: string; color: string; bg: string; pulse: string } {
  if (isLoading) return { text: t('badge.loading'), color: "text-[hsl(var(--brand-primary))]", bg: "bg-[hsl(var(--brand-primary)/0.1)] border-[hsl(var(--brand-primary)/0.2)]", pulse: "bg-[hsl(var(--brand-primary))]" };
  if (openIssues > 0) {
    return { text: t('badge.issues', { count: openIssues }), color: "text-yellow-600", bg: "bg-yellow-600/10 border-yellow-600/20", pulse: "bg-yellow-600" };
  }
  return { text: t('badge.allGood'), color: "text-green-600", bg: "bg-green-600/10 border-green-600/20", pulse: "bg-green-600" };
}

function computeWarrantyCoverage(isLoading: boolean, totalAssets: number, expiredCount: number): string {
  if (isLoading) return '—';
  if (totalAssets === 0) return '0%';
  return `${Math.round((totalAssets - expiredCount) / totalAssets * 100)}%`;
}

function computeAssetOverviewSubtitle(
  isLoading: boolean,
  categoryCount: number,
  t: (key: string) => string,
): string {
  if (isLoading) return t('stats.loading');
  if (categoryCount === 0) return t('sections.noAssetsAdded');
  return t('sections.assetsGrouped');
}

interface StatConfig {
  label: string;
  value: string;
  change: string;
  trend: 'up' | 'down';
  icon: React.ElementType;
  subtitle: string;
}

interface StatsData {
  totalAssetsChange: string;
  warrantyChange: string;
  warrantyCoverage: string;
  maintenanceTrend: 'up' | 'down';
  maintenanceChange: string;
  issuesTrend: 'up' | 'down';
  issuesChange: string;
  assetTrackedText: string;
  assetOverviewSubtitle: string;
  statsConfig: StatConfig[];
}

function computeStatsData(
  isLoading: boolean,
  totalAssets: number,
  dueMaintenance: number,
  openIssues: number,
  expiredCount: number,
  assetCategoryCount: number,
  t: (key: string, options?: Record<string, unknown>) => string,
): StatsData {
  const totalAssetsChange = totalAssets === 0 ? t('stats.noneYet') : t('stats.items', { count: totalAssets });
  const warrantyCoverage = computeWarrantyCoverage(isLoading, totalAssets, expiredCount);
  const warrantyChange = totalAssets > 0 ? t('stats.protected', { count: totalAssets }) : t('stats.addAssetsToTrack');
  const maintenanceTrend: 'up' | 'down' = dueMaintenance === 0 ? 'up' : 'down';
  const maintenanceChange = dueMaintenance === 0 ? t('stats.onTrack') : t('stats.taskThisWeek', { count: dueMaintenance });
  const issuesTrend: 'up' | 'down' = openIssues === 0 ? 'up' : 'down';
  const issuesChange = openIssues === 0 ? t('stats.noOpenIssues') : t('stats.needAttention', { count: openIssues });
  const assetTrackedText = isLoading ? t('stats.loading') : t('stats.assetsTracked', { count: totalAssets });
  const assetOverviewSubtitle = computeAssetOverviewSubtitle(isLoading, assetCategoryCount, t);
  const statsConfig: StatConfig[] = [
    {
      label: t('stats.totalAssets'),
      value: isLoading ? '—' : String(totalAssets),
      change: totalAssetsChange,
      trend: 'up',
      icon: Package,
      subtitle: t('stats.totalAssetsSubtitle'),
    },
    {
      label: t('stats.warrantyCoverage'),
      value: warrantyCoverage,
      change: warrantyChange,
      trend: 'up',
      icon: Shield,
      subtitle: t('stats.warrantyCoverageSubtitle'),
    },
    {
      label: t('stats.dueMaintenance'),
      value: isLoading ? '—' : String(dueMaintenance),
      change: maintenanceChange,
      trend: maintenanceTrend,
      icon: Calendar,
      subtitle: t('stats.dueMaintenanceSubtitle'),
    },
    {
      label: t('stats.openIssues'),
      value: isLoading ? '—' : String(openIssues),
      change: issuesChange,
      trend: issuesTrend,
      icon: AlertTriangle,
      subtitle: t('stats.openIssuesSubtitle'),
    },
  ];
  return { totalAssetsChange, warrantyChange, warrantyCoverage, maintenanceTrend, maintenanceChange, issuesTrend, issuesChange, assetTrackedText, assetOverviewSubtitle, statsConfig };
}

function StatCards({ isLoading, configs }: Readonly<{ isLoading: boolean; configs: StatConfig[] }>) {
  if (isLoading) return <>{['s1', 's2', 's3', 's4'].map((k) => <StatSkeleton key={k} />)}</>;
  return (
    <>
      {configs.map((stat, index) => {
        const Icon = stat.icon;
        const TrendIcon = stat.trend === 'up' ? TrendingUp : TrendingDown;
        const trendColor = stat.trend === 'up' ? 'text-green-600' : 'text-yellow-600';
        return (
          <div
            key={stat.label}
            className={cn(
              'relative rounded-2xl border bg-card p-6 hover:shadow-lg transition-shadow',
              'opacity-0 animate-fade-in-up',
            )}
            style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-xs font-medium text-muted-foreground tracking-wider uppercase">
                  {stat.label}
                </p>
                <p className="text-3xl font-normal tracking-tight mt-2">{stat.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{stat.subtitle}</p>
                <div className="flex items-center gap-1 mt-3">
                  <TrendIcon className={cn('h-3 w-3', trendColor)} />
                  <span className={cn('text-xs font-medium', trendColor)}>{stat.change}</span>
                </div>
              </div>
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/50 flex items-center justify-center flex-shrink-0">
                <Icon className="h-5 w-5" />
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}

function getTaskIconBg(isOverdue: boolean, priority: string): string {
  if (isOverdue || priority === "high") return "bg-red-600/10 border border-red-600/20";
  if (priority === "medium") return "bg-yellow-600/10 border border-yellow-600/20";
  return "bg-green-600/10 border border-green-600/20";
}

function getTaskDotColor(isOverdue: boolean, priority: string): string {
  if (isOverdue || priority === "high") return "bg-red-600";
  if (priority === "medium") return "bg-yellow-600";
  return "bg-green-600";
}

// ─── Section content helpers (extracted to reduce Dashboard cognitive complexity) ──

function warrantyStatus(asset: Asset): 'active' | 'expiring' | 'expired' | 'none' {
  if (!asset.warrantyExpiresAt) return 'none';
  const exp = new Date(asset.warrantyExpiresAt);
  const now = new Date();
  const daysLeft = Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (daysLeft < 0) return 'expired';
  if (daysLeft <= 90) return 'expiring';
  return 'active';
}

function AssetsSectionContent({ isLoading, assets, navigate, t }: Readonly<{
  isLoading: boolean;
  assets: Asset[];
  navigate: (path: string) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}>) {
  if (isLoading) return <SectionSkeleton rows={3} />;
  if (assets.length === 0) return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="h-14 w-14 rounded-full bg-secondary/50 flex items-center justify-center mb-4">
        <Package className="h-7 w-7 text-muted-foreground" />
      </div>
      <p className="text-sm text-muted-foreground mb-4">{t('assetsOverview.noAssets')}</p>
      <Button size="sm" variant="outline" onClick={() => navigate(ROUTES.ASSETS)} className="text-xs">
        {t('assetsOverview.addFirstAsset')} <ArrowRight className="h-3 w-3 ml-2" />
      </Button>
    </div>
  );

  const recent = [...assets]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 3);

  return (
    <div className="space-y-3">
      {recent.map((asset) => {
        const imgSrc = toAbsoluteImageUrl(asset.productImageUrl ?? asset.imageUrl);
        const ws = warrantyStatus(asset);
        const warrantyBadge = {
          active:   { label: 'Warranty Active',   cls: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-400' },
          expiring: { label: 'Expiring Soon',      cls: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/20 dark:text-amber-400' },
          expired:  { label: 'Warranty Expired',   cls: 'bg-red-50 text-red-600 border-red-200 dark:bg-red-950/20 dark:text-red-400' },
          none:     { label: 'No Warranty',        cls: 'bg-muted text-muted-foreground border-border' },
        }[ws];

        return (
          <button
            key={asset.id}
            type="button"
            className="group w-full text-left flex items-center gap-4 p-4 rounded-xl border border-border/50 bg-card hover:border-border hover:shadow-md transition-all"
            onClick={() => navigate(`${ROUTES.ASSETS}/${asset.id}`)}
          >
            {/* Image */}
            <div className="h-14 w-14 rounded-xl border bg-muted/30 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {imgSrc ? (
                <img src={imgSrc} alt={asset.name} referrerPolicy="no-referrer"
                  className="h-full w-full object-contain p-1.5"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
              ) : (
                <Package className="h-6 w-6 text-muted-foreground" />
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{asset.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {[asset.brand, asset.category].filter(Boolean).join(' · ')}
              </p>
              <span className={cn('inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full border text-[10px] font-medium', warrantyBadge.cls)}>
                <Shield className="h-2.5 w-2.5" />
                {warrantyBadge.label}
              </span>
            </div>

            {/* Health score */}
            {asset.healthScore != null && (
              <div className="flex-shrink-0 text-right">
                <p className={cn('text-lg font-semibold tabular-nums',
                  asset.healthScore >= 85 ? 'text-green-600' :
                  asset.healthScore >= 60 ? 'text-amber-600' : 'text-red-600'
                )}>{asset.healthScore}</p>
                <p className="text-[10px] text-muted-foreground">Health</p>
              </div>
            )}

            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 group-hover:text-foreground transition-colors" />
          </button>
        );
      })}
    </div>
  );
}

function MaintenanceSectionContent({ isLoading, tasks, navigate, t }: Readonly<{
  isLoading: boolean;
  tasks: UpcomingTask[];
  navigate: (path: string) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}>) {
  if (isLoading) return <SectionSkeleton rows={3} />;
  if (tasks.length === 0) return (
    <div className="p-4 rounded-xl bg-gradient-to-r from-green-600/10 to-transparent border border-green-600/20">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="text-sm font-normal text-green-600">{t('maintenance.onTrack')}</h4>
          <p className="text-xs text-muted-foreground mt-1">
            {t('maintenance.noTasks')}
          </p>
        </div>
      </div>
    </div>
  );
  return (
    <div className="space-y-3">
      {tasks.map((task) => {
        const taskIconBg = getTaskIconBg(task.isOverdue, task.priority);
        const taskDotColor = getTaskDotColor(task.isOverdue, task.priority);
        const taskTimeColor = task.isOverdue ? "text-red-600 font-medium" : "text-muted-foreground";
        return (
          <button
            key={task.id}
            type="button"
            className="flex items-center gap-4 p-4 rounded-xl border hover:bg-secondary/20 transition-colors w-full text-left group"
            onClick={() => navigate(ROUTES.MAINTENANCE)}
          >
            <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0", taskIconBg)}>
              <div className={cn("h-3 w-3 rounded-full", taskDotColor)} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-sm group-hover:text-foreground transition-colors">{task.title}</h3>
              {task.assetName && <p className="text-xs text-muted-foreground mt-0.5">{task.assetName}</p>}
            </div>
            <div className={cn("flex items-center gap-2 text-xs flex-shrink-0", taskTimeColor)}>
              <Clock className="h-3.5 w-3.5" />
              {task.dueDate}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export function Dashboard() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const { currentProperty } = useProperty();
  const [greeting, setGreeting] = useState(getGreeting(t));
  const {
    assets,
    totalAssets,
    dueMaintenance,
    openIssues,
    assetsByCategory,
    upcomingTasks,
    isLoading,
    refresh,
  } = useDashboardData();

  useEffect(() => {
    setGreeting(getGreeting(t));
    const interval = setInterval(() => setGreeting(getGreeting(t)), 60000);
    return () => clearInterval(interval);
  }, [t]);

  // ── Dynamic hero content ──────────────────────────────────────────────────
  const heroSubtitle = computeHeroSubtitle(isLoading, openIssues, dueMaintenance, totalAssets, t);
  const badgeConfig = computeBadgeConfig(isLoading, openIssues, t);

  // ── Pre-computed derived values ────────────────────────────────────────────
  const expiredCount = assetsByCategory.find((c) => c.category === 'Expired')?.count ?? 0;
  const {
    assetTrackedText,
    assetOverviewSubtitle,
    statsConfig,
  } = computeStatsData(isLoading, totalAssets, dueMaintenance, openIssues, expiredCount, assetsByCategory.length, t);

  // ── Quick actions (static navigation) ────────────────────────────────────
  const quickActions = [
    { label: t('quickActions.addAsset'), description: t('quickActions.addAssetDesc'), icon: Package, route: ROUTES.ASSETS },
    { label: t('quickActions.maintenance'), description: t('quickActions.maintenanceDesc'), icon: Calendar, route: ROUTES.MAINTENANCE },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* ── Hero Section ── */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-secondary/30 to-background">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 md:py-20">
          <div className="max-w-4xl">
            {/* Dynamic status badge */}
            <div className={cn("inline-flex items-center gap-2 rounded-full border px-4 py-1.5 mb-6", badgeConfig.bg)}>
              <div className="relative flex h-2 w-2">
                <span className={cn("animate-ping absolute inline-flex h-full w-full rounded-full opacity-75", badgeConfig.pulse)} />
                <span className={cn("relative inline-flex rounded-full h-2 w-2", badgeConfig.pulse)} />
              </div>
              <span className={cn("text-xs font-medium tracking-wide", badgeConfig.color)}>
                {badgeConfig.text}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-tight">
              {greeting},
              <br />
              <span className="text-muted-foreground font-extralight">
                {currentProperty?.city
                    ? t('hero.cityHome', { city: currentProperty.city })
                    : t('hero.defaultHome')}
              </span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
              {heroSubtitle}
            </p>

            {/* Trust indicators strip */}
            <div className="flex flex-wrap items-center gap-6 mt-8 text-sm text-muted-foreground">
              {currentProperty && (
                <div className="flex items-center gap-2">
                  <Home className="h-4 w-4 text-green-600" />
                  <span>{currentProperty.name || currentProperty.address || currentProperty.city}</span>
                </div>
              )}
              <div className="hidden sm:block h-4 w-px bg-border" />
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-muted-foreground/60" />
                <span>{t('sections.dataSecurity')}</span>
              </div>
              <div className="hidden sm:block h-4 w-px bg-border" />
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-muted-foreground/60" />
                <span>{assetTrackedText}</span>
              </div>
              <div className="hidden sm:block h-4 w-px bg-border" />
              <button
                onClick={refresh}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                aria-label={t('a11y.refreshDashboard')}
              >
                <RefreshCw className="h-3.5 w-3.5" />
                <span>{t('sections.refresh')}</span>
              </button>
            </div>
          </div>

          <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-green-600/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 h-48 w-48 rounded-full bg-[hsl(var(--brand-primary)/0.08)] blur-3xl pointer-events-none" />
        </div>
      </section>

      {/* ── Promotional Ads (below hero) ── */}
      <PromoAdsCarousel assets={assets} />

      {/* ── Stats Grid ── */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-12">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCards isLoading={isLoading} configs={statsConfig} />
        </div>
      </section>

      {/* ── Critical Alerts (parity with app home) ── */}
      {!isLoading && <CriticalAlertsSection assets={assets} />}

      {/* ── Main Content Grid ── */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 pb-12">
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left Column - Asset Health & Tasks */}
          <div className="lg:col-span-2 space-y-6">
            {/* Asset Health Overview */}
            <div
              className="rounded-2xl border bg-card p-6 opacity-0 animate-fade-in-up"
              style={{ animationDelay: "0.6s", animationFillMode: "forwards" }}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-normal tracking-tight">{t('sections.assetOverview')}</h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    {isLoading ? t('stats.loading') : assets.length > 0 ? `${Math.min(assets.length, 3)} most recently added` : t('assetsOverview.noAssets')}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate(ROUTES.ASSETS)} className="text-xs">
                  {t('sections.viewAll')} <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              </div>

              <AssetsSectionContent isLoading={isLoading} assets={assets} navigate={navigate} t={t} />
            </div>

            {/* Upcoming Maintenance Tasks */}
            <div
              className="rounded-2xl border bg-card p-6 opacity-0 animate-fade-in-up"
              style={{ animationDelay: "0.7s", animationFillMode: "forwards" }}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-normal tracking-tight">{t('sections.upcomingMaintenance')}</h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    {isLoading ? t('stats.loading') : t('sections.scheduledTasks')}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate(ROUTES.MAINTENANCE)} className="text-xs">
                  {t('sections.viewAll')} <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              </div>

              <MaintenanceSectionContent isLoading={isLoading} tasks={upcomingTasks} navigate={navigate} t={t} />
            </div>
          </div>

          {/* Right Column - Critical Alerts, Quick Actions & Activity */}
          <div className="space-y-6">
            {/* Critical Alerts (renders nothing when there are no alerts) */}
            <CriticalAlertsSection assets={assets} />

            {/* Quick Actions */}
            <div
              className="rounded-2xl border bg-gradient-to-br from-card to-secondary/20 p-6 opacity-0 animate-fade-in-up"
              style={{ animationDelay: "0.8s", animationFillMode: "forwards" }}
            >
              <div className="mb-6">
                <h2 className="text-xl font-normal tracking-tight">{t('sections.quickActions')}</h2>
                <p className="text-xs text-muted-foreground mt-1">{t('sections.quickActionsSubtitle')}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {quickActions.map((action) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={action.label}
                      onClick={() => navigate(action.route)}
                      className="flex flex-col items-start gap-3 p-4 rounded-xl border bg-secondary/10 hover:bg-gradient-to-br hover:from-secondary/60 hover:to-secondary/20 hover:border-secondary transition-all text-left group"
                    >
                      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-foreground to-foreground/80 text-background flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-normal text-sm">{action.label}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">{action.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* CTA Card */}
            <div
              className="rounded-2xl border bg-gradient-to-br from-foreground to-foreground/90 text-background p-6 opacity-0 animate-fade-in-up relative overflow-hidden"
              style={{ animationDelay: "1s", animationFillMode: "forwards" }}
            >
              <div className="absolute top-0 right-0 h-32 w-32 bg-background/10 rounded-full -mr-16 -mt-16" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                  <Wrench className="h-5 w-5" />
                  <h3 className="font-normal text-lg">{t('cta.title')}</h3>
                </div>
                <p className="text-sm text-background/90 mb-4 leading-relaxed">
                  {t('cta.description')}
                </p>
                <Button
                  onClick={() => navigate(ROUTES.SERVICES)}
                  variant="outline"
                  className="w-full bg-background text-foreground hover:bg-background/90 border-0 shadow-lg"
                >
                  {t('cta.button')} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
